import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Checkbox, Dropdown, Form, Grid, Input, Popup, TextArea} from "semantic-ui-react";
import CityInput from "../../../components/CityInput";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import SelectPointModal from "./selectPointModal";
import PvzSelectMapModal from "./pvzSelectMapModal";
import SectionForm from "./sectionForm";
import {stringToAddress} from "./functions/addresses";
import {buildAddressFromPvz, getMapApiEntries, resolveParcelWeightForPvz, resolvePvzSearchLocation} from "./functions/pvzAddress";
import {splitContactFio} from "./functions/contactFio";
import Info from "../../../components/Info";
import InputDropdown from "../../../components/Dropdowns/InputDropdown";
import SemanticCountryPhoneInput from "../../../controls/semanticCountryPhoneInput";
import SemanticEmailInput, {getEmailValidationError} from "../../../controls/semanticEmailInput";
import {getCarrierUiExtension} from "./carrierExtensions";
import {apiPost} from "../../../services/apiClient";
import {toast} from "react-toastify";

const EMAIL_FORMAT_HINT = 'Укажите адрес в формате name@example.ru';

const normalizePvzAddressDisplay = (addressFull) => {
    const source = String(addressFull || '').trim();
    if (!source) return '';

    const normalizeToken = (v) => String(v || '')
        .toLowerCase()
        .replace(/^город\s+/i, '')
        .replace(/^г\.\s*/i, '')
        .replace(/^г\s+/i, '')
        .trim();

    const parts = source
        .split(',')
        .map(x => x.trim())
        .filter(Boolean);

    const deduped = [];
    for (let i = 0; i < parts.length; i++) {
        const prev = deduped.length ? deduped[deduped.length - 1] : null;
        if (prev && normalizeToken(prev) === normalizeToken(parts[i])) {
            continue;
        }
        deduped.push(parts[i]);
    }

    return deduped.join(', ');
};

const PointSection = ({icon, label, form, changeParams, name, companyId, errors: errorsProp, isCreate, onChangeAddress, setFieldError}) => {
    const errors = errorsProp || {};
    const formRef = useRef(form);
    formRef.current = form;
    const isRu = () => (form[`${name}Address`] || {}).countryCode === 'RU';
    const [isOpenAddressBookModal, setIsOpenAddressBookModal] = useState(false);
    const [isOpenStringAddressModal, setIsOpenStringAddressModal] = useState(false);
    const [postalCodeIdDisabled, setPostalCodeIsDisabled] = useState(isRu() && !!(form[`${name}Address`] || {}).postalCode);
    const [stringAddress, setStringAddress] = useState('');
    const carrierExtension = getCarrierUiExtension(form.selectedTariff);
    const phoneHintText = carrierExtension.getPhoneHintText(form.selectedTariff);
    const showCourierMessage = isCreate && name === 'shipment' && carrierExtension.isCourierMessageAvailable(form.selectedTariff);
    const courierMessageInfoText = 'Курьер увидит этот текст еще до принятия заказа на доставку, что позволит исполнителю оценить: сможет ли он выполнить заказ. Сообщение должно быть очень коротким. Наши клиенты обычно указывают в этом поле фразы: "Бортовая Газель", "Водитель с Мед.книжкой", "нужна тележка" и т.д.';
    const senderCommentValue = form.senderComment || '';
    const senderCommentMaxLength = 50;
    const senderCommentEnabled = !!form.senderCommentEnabled;
    const isSenderCommentMaxReached = senderCommentValue.length >= senderCommentMaxLength;
    // На создании обычно используется form.deliveryType (код),
    // на просмотре может приходить form.deliveryTypeCode.
    const deliveryTypeCode = String(
        form.deliveryTypeCode != null && form.deliveryTypeCode !== ''
            ? form.deliveryTypeCode
            : (form.deliveryType || '')
    );
    const selectedCarrierName = (form.selectedTariff || {}).carrierName;
    const selectedCarrierServiceName = (form.selectedTariff || {}).carrierServiceName;
    const selectedCarrierCode = (form.selectedTariff || {}).carrierCode;
    const isPvzCdekCarrier = selectedCarrierName === 'ПВЗ-СДЭК' || selectedCarrierServiceName === 'ПВЗ-СДЭК';
    const isCdekCarrier = isPvzCdekCarrier || selectedCarrierCode === 'CDEK';
    const hasExistingPvzPointCode = !!String(form[`${name}PointCode`] || '').trim();
    const requiresPickupPointCode =
        (isCreate ? isPvzCdekCarrier : (isCdekCarrier || hasExistingPvzPointCode)) && (
            (name === 'shipment' && ['4', '6'].includes(deliveryTypeCode))
            || (name === 'delivery' && ['5', '6'].includes(deliveryTypeCode))
        );
    const showAdditionalPhoneFields = !requiresPickupPointCode && !carrierExtension.usePrimaryPhoneOnly(form.selectedTariff);

    const getRouteKindForSection = () => {
        switch (deliveryTypeCode) {
            case '4':
                return name === 'shipment' ? 'pvz' : 'door';
            case '5':
                return name === 'shipment' ? 'door' : 'pvz';
            case '6':
                return 'pvz';
            default:
                return null;
        }
    };

    const routeKindForSection = getRouteKindForSection();

    const mergedAddressForDisplay = useMemo(() => {
        const merged = {
            ...(form[`${name}City`] || {}),
            ...(form[`${name}Address`] || {})
        };
        const location = resolvePvzSearchLocation(form, name);
        if (location.city && !merged.city) {
            merged.city = location.city;
        }
        return merged;
    }, [
        form[`${name}City`],
        form[`${name}Address`]
    ]);

    const pvzSearchLocation = useMemo(
        () => resolvePvzSearchLocation(form, name),
        [
            form[`${name}City`],
            form[`${name}Address`]
        ]
    );

    const formForCityInput = useMemo(() => ({
        ...form,
        [`${name}Address`]: mergedAddressForDisplay
    }), [form, name, mergedAddressForDisplay]);

    const renderSectionRouteBadge = () => {
        if (!routeKindForSection) return null;
        const isPvz = routeKindForSection === 'pvz';
        return (
            <span className={`section-form__route-badge ${isPvz ? 'section-form__route-badge_pvz' : 'section-form__route-badge_door'}`}>
                {isPvz ? (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M3 14H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M4 14V4.5C4 4.22386 4.22386 4 4.5 4H11.5C11.7761 4 12 4.22386 12 4.5V14" stroke="currentColor" strokeWidth="1.5"/>
                        <rect x="6" y="6" width="1.5" height="1.5" fill="currentColor"/>
                        <rect x="8.5" y="6" width="1.5" height="1.5" fill="currentColor"/>
                        <rect x="6" y="8.5" width="1.5" height="1.5" fill="currentColor"/>
                        <rect x="8.5" y="8.5" width="1.5" height="1.5" fill="currentColor"/>
                        <path d="M7.5 14V11.5C7.5 11.2239 7.72386 11 8 11V11C8.27614 11 8.5 11.2239 8.5 11.5V14" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                ) : (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M2 4.5H9.5V10H2V4.5Z" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M9.5 6H12.2L14 7.9V10H9.5V6Z" stroke="currentColor" strokeWidth="1.5"/>
                        <circle cx="5" cy="11.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                        <circle cx="11.5" cy="11.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                )}
                <span>{isPvz ? 'ПВЗ' : 'Дверь'}</span>
            </span>
        );
    };

    const [pvzUiInfo, setPvzUiInfo] = useState(null);
    const [nearestPvz, setNearestPvz] = useState([]);
    const [nearestPvzLoading, setNearestPvzLoading] = useState(false);
    const [nearestPvzValue, setNearestPvzValue] = useState(null);
    const [lockedNearestPvz, setLockedNearestPvz] = useState(null);
    const [isPvzMapOpen, setIsPvzMapOpen] = useState(false);

    const pvzDetailsKey = `${name}PvzDetails`;

    const mapDetailsToPvzUiInfo = (details, fallbackCode, fallbackAddress) => {
        const code = String((details && (details.code || details.Code)) || fallbackCode || '').trim().toUpperCase();
        if (!code && !fallbackAddress) {
            return null;
        }

        const addressFull = normalizePvzAddressDisplay(
            (details && (details.addressFull || details.AddressFull)) || fallbackAddress || ''
        );

        return {
            code,
            name: (details && (details.name || details.Name)) || '',
            addressFull,
            workTime: (details && (details.workTime || details.WorkTime)) || '',
            site: (details && (details.site || details.Site)) || '',
            phonesJson: (details && (details.phonesJson || details.PhonesJson)) || '',
            weightMax: details && (details.weightMax != null ? details.weightMax : details.WeightMax)
        };
    };

    const renderPvzInfoBlock = () => {
        if (!pvzUiInfo) {
            return null;
        }

        return (
            <Grid.Row columns={16}>
                <Grid.Column width={16}>
                    <div style={{
                        fontSize: '13px',
                        color: '#475566',
                        marginTop: '8px',
                        padding: '12px',
                        background: '#f4f8fc',
                        borderRadius: '4px',
                        border: '1px solid #d4e5f5'
                    }}
                    >
                        <div style={{fontWeight: 600, marginBottom: '8px'}}>Информация по ПВЗ</div>
                        <div><b>Код ПВЗ:</b> {pvzUiInfo.code || '-'}</div>
                        <div><b>Адрес:</b> {pvzUiInfo.addressFull || '-'}</div>
                        {!!pvzUiInfo.workTime && <div><b>Часы работы:</b> {pvzUiInfo.workTime}</div>}
                        {pvzUiInfo.weightMax != null && pvzUiInfo.weightMax !== '' && (
                            <div><b>Макс. вес:</b> {pvzUiInfo.weightMax} кг</div>
                        )}
                        {!!pvzUiInfo.site && (
                            <div>
                                <b>Сайт ТК:</b>{' '}
                                <a
                                    target='_blank'
                                    rel='noreferrer'
                                    href={pvzUiInfo.site}
                                    style={{color: '#4a74a7'}}
                                >
                                    Перейти
                                </a>
                            </div>
                        )}
                    </div>
                </Grid.Column>
            </Grid.Row>
        );
    };

    useEffect(() => {
        if (!requiresPickupPointCode) {
            setPvzUiInfo(null);
            setNearestPvz([]);
            setNearestPvzLoading(false);
            setNearestPvzValue(null);
            setLockedNearestPvz(null);
            setIsPvzMapOpen(false);
        }
    }, [requiresPickupPointCode]);

    useEffect(() => {
        if (!requiresPickupPointCode || isCreate) return;

        const code = String(form[`${name}PointCode`] || '').trim().toUpperCase();
        const fullName = normalizePvzAddressDisplay(((form[`${name}Address`] || {}).fullName) || '');
        const details = form[pvzDetailsKey];

        if (details) {
            setPvzUiInfo(mapDetailsToPvzUiInfo(details, code, fullName));
            setNearestPvzValue(code || null);
            return;
        }

        if (!code && !fullName) {
            setPvzUiInfo(null);
            setNearestPvzValue(null);
            return;
        }

        setPvzUiInfo(mapDetailsToPvzUiInfo(null, code, fullName));
        setNearestPvzValue(code || null);

        if (!code) {
            return;
        }

        let cancelled = false;
        apiPost('parcels/cdek/pickuppoints/by-code', {code})
            .then((data) => {
                if (cancelled || !data) return;
                setPvzUiInfo(mapDetailsToPvzUiInfo(data, code, fullName));
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [
        requiresPickupPointCode,
        isCreate,
        form[`${name}PointCode`],
        form[pvzDetailsKey],
        ((form[`${name}Address`] || {}).fullName)
    ]);

    const applyPvzSelection = (pvz, opts) => {
        const code = (((pvz && pvz.code) || (pvz && pvz.Code) || '')).toUpperCase();
        const lockEntries = opts && (opts.lockEntries || opts.entries);
        if (!code) {
            setLockedNearestPvz(null);
        } else if (lockEntries && lockEntries.length) {
            setLockedNearestPvz(lockEntries);
        }
        const pvzName = (pvz && (pvz.name || pvz.Name)) || '';
        const pvzAddressFullRaw = (pvz && (pvz.addressFull || pvz.AddressFull)) || '';
        const pvzAddressFull = normalizePvzAddressDisplay(pvzAddressFullRaw);
        const pvzWorkTime = (pvz && (pvz.workTime || pvz.WorkTime)) || '';
        const pvzSite = (pvz && (pvz.site || pvz.Site)) || '';
        const pvzPhonesJson = (pvz && (pvz.phonesJson || pvz.PhonesJson)) || '';

        setPvzUiInfo(code ? {
            code,
            name: pvzName,
            addressFull: pvzAddressFull,
            workTime: pvzWorkTime,
            site: pvzSite,
            phonesJson: pvzPhonesJson
        } : null);

        const existingComment = form[`${name}AddressComment`] || '';
        const commentWithoutPvzLine = existingComment
            .split('\n')
            .map(l => l.trim())
            .filter(l => l && !/^ПВЗ:/i.test(l))
            .join('\n');

        const prevAddr = form[`${name}Address`] || {};
        const nextAddress = code
            ? {
                ...buildAddressFromPvz(pvz, pvzAddressFull, prevAddr),
                pointCode: code
            }
            : prevAddr;

        changeParams({
            [`${name}PointCode`]: code,
            ...(code ? {[`${name}Address`]: nextAddress, [`${name}AddressComment`]: commentWithoutPvzLine} : {})
        });
        setNearestPvzValue(code || null);
    };

    useEffect(() => {
        if (!requiresPickupPointCode) return;

        setLockedNearestPvz(null);
        const location = resolvePvzSearchLocation(formRef.current || form, name);
        const postalCode = location.postalCode;
        const city = location.city;
        const centerLat = location.centerLat;
        const centerLon = location.centerLon;
        if (!postalCode && !city) {
            setNearestPvz([]);
            return;
        }

        const endpoint = name === 'shipment'
            ? 'parcels/cdek/pickuppoints/map/from'
            : 'parcels/cdek/pickuppoints/map/to';
        const request = {
            postalCode,
            city,
            centerLat: centerLat == null ? null : Number(centerLat),
            centerLon: centerLon == null ? null : Number(centerLon),
            fullList: true,
            weight: resolveParcelWeightForPvz(formRef.current || form)
        };

        setNearestPvzLoading(true);
        apiPost(endpoint, request)
            .then((data) => {
                const entries = getMapApiEntries(data);
                if (data && (data.warningMessage || data.WarningMessage)) {
                    toast.warning(data.warningMessage || data.WarningMessage);
                }
                setNearestPvz(entries);
                const selectedCode = (formRef.current[`${name}PointCode`] || '').toString().toUpperCase();
                const keepSelection = selectedCode && entries.some((e) =>
                    String(e.code || e.Code || '').toUpperCase() === selectedCode);
                setNearestPvzValue(keepSelection ? selectedCode : null);
                setNearestPvzLoading(false);
            })
            .catch((err) => {
                setNearestPvz([]);
                setNearestPvzValue(null);
                setNearestPvzLoading(false);
                toast.error('Не удалось загрузить список ПВЗ');
                console.log(err);
            });
    }, [
        requiresPickupPointCode,
        name,
        (form[`${name}Address`] || {}).postalCode,
        (form[`${name}Address`] || {}).city,
        (form[`${name}Address`] || {}).fullName,
        (form[`${name}Address`] || {}).geoLat,
        (form[`${name}Address`] || {}).geoLon,
        (form[`${name}City`] || {}).city,
        (form[`${name}City`] || {}).fullName,
        (form[`${name}City`] || {}).postalCode,
        form.weight,
        form.cargoWeight,
        form.documentWeight
    ]);

    const listForNearestDropdown = (lockedNearestPvz && lockedNearestPvz.length) ? lockedNearestPvz : nearestPvz;

    const addrForPvzHint = form[`${name}Address`] || {};
    const hasHouseForPvz = !!(addrForPvzHint.house && String(addrForPvzHint.house).trim())
        || !!(addrForPvzHint.building && String(addrForPvzHint.building).trim());
    const hasGeoForPvz = addrForPvzHint.geoLat != null && addrForPvzHint.geoLon != null;
    const listByDistanceMode = hasHouseForPvz && hasGeoForPvz;
    const nearestPvzHintText = listByDistanceMode
        ? 'Ищем ПВЗ рядом с уточнённой точкой (выберите из списка)'
        : 'Список ПВЗ для выбранного населённого пункта (код или адрес — только из списка ниже)';

    const nearestPvzOptions = useMemo(() => listForNearestDropdown.map((pvz, idx) => {
        const code = ((pvz.code || pvz.Code) || '').toUpperCase();
        const address = normalizePvzAddressDisplay(pvz.addressFull || pvz.AddressFull || '-') || '-';
        const dk = pvz.distanceKm != null ? pvz.distanceKm : pvz.DistanceKm;
        const distPart = listByDistanceMode && dk != null && Number.isFinite(Number(dk))
            ? ` · ${Number(dk).toFixed(1)} км`
            : '';
        const textWithoutDistance = `${code} - ${address}`;
        return {
            key: `${code || 'pvz'}_${idx}`,
            value: code,
            // Важно: поиск в Dropdown идет по text, поэтому дистанцию туда не добавляем.
            // Иначе поиск может матчить "2.4 км" вместо кода/адреса.
            text: textWithoutDistance,
            content: `${textWithoutDistance}${distPart}`
        };
    }), [listForNearestDropdown, listByDistanceMode]);

    const patchContactFio = (field, value) => {
        const lastKey = `${name}ContactLastName`;
        const firstKey = `${name}ContactFirstName`;
        const patKey = `${name}ContactPatronymic`;
        const personKey = `${name}ContactPersonName`;
        const last = field === lastKey ? value : (form[lastKey] || '');
        const first = field === firstKey ? value : (form[firstKey] || '');
        const pat = field === patKey ? value : (form[patKey] || '');
        const joined = [last, first, pat].filter((x) => x && String(x).trim()).join(' ');
        changeParams({ [field]: value, [personKey]: joined });
    };

    const patchContactPersonName = (value) => {
        const fio = splitContactFio(value);
        changeParams({
            [`${name}ContactPersonName`]: value,
            [`${name}ContactLastName`]: fio.lastName,
            [`${name}ContactFirstName`]: fio.firstName,
            [`${name}ContactPatronymic`]: fio.patronymic
        });
    };

    // Функция для определения, используется ли перевозчик КСЭ
    const isCSECarrier = () => {
        const carrierCode = form.selectedTariff && form.selectedTariff.carrierCode;
        return carrierCode && (
            carrierCode === 'CSE' ||
            carrierCode.includes('КСЭ') ||
            carrierCode.includes('Cse')
        );
    };

    // Получить максимальную длину комментария к адресу для КСЭ
    const getAddressCommentMaxLength = () => {
        return isCSECarrier() ? 100 : 255;
    };

    const changeCity = (nameParam, value, values = {}) => {
        const nextAddress = {
            ...(form[`${name}Address`] || {}),
            ...values,
            [nameParam.includes('AddressCountry') ? 'countryCode' : nameParam]: value,
            id: null
        };
        const params = {
            [`${name}Address`]: nextAddress
        };
        if (nameParam === 'city' || nameParam === 'cityName') {
            params[`${name}City`] = {
                ...(form[`${name}City`] || {}),
                ...nextAddress
            };
        }
        setPostalCodeIsDisabled(isRu() ? (nameParam !== 'postalCode' && params[`${name}Address`].postalCode) : false);
        changeParams(params);
    }

    const errorText = 'Адрес отправления не найден. Пожалуйста, внесите корректировки';

    return (
        <SectionForm
            actions={isCreate && !requiresPickupPointCode && <div className='total__change-btn'>
                <div className='total__change-btn_name' onClick={() => setIsOpenAddressBookModal(true)}>Из адресной
                    книги
                </div>
                <div className='total__change-btn_name' onClick={() => setIsOpenStringAddressModal(true)}>Адрес одной
                    строкой
                </div>
            </div>}
            icon={icon}
            label={label}
            headerAddon={renderSectionRouteBadge()}
        >
            <Form className={isCreate ? '' : 'point-view'}>
                <Grid>
                    <Grid.Row columns={16}>
                        <Grid.Column width={requiresPickupPointCode ? 10 : 4}>
                            <Form.Field className={errors[`${name}Address.city`] ? 'error-style' : ''}>
                                <label>
                                    {requiresPickupPointCode ? 'ПВЗ в населенном пункте*' : 'Населенный пункт*'}
                                </label>
                                <CityInput
                                    isDisabled={!isCreate}
                                    name={name}
                                    value={formForCityInput}
                                    onChange={(e, {name, value}, values) => changeCity(name, value, values)}
                                />
                            </Form.Field>
                        </Grid.Column>
                        {requiresPickupPointCode && <Grid.Column width={6} />}
                        {!requiresPickupPointCode && (
                            <Fragment>
                                <Grid.Column width={6}>
                                    <Form.Field error={errors[`${name}Address.street`]}>
                                        <label>
                                            Улица
                                        </label>
                                        <InputDropdown
                                            name={'street'}
                                            url={'parcels/addresses/streetAddressSuggestions'}
                                            disabled={!isCreate}
                                            value={(form[`${name}Address`] || {}).street || ''}
                                            onChange={changeCity}
                                            params={form[`${name}Address`]}
                                            paramsForSearchFields={[['city', 'cityName'], ['countryCode']]}
                                            dependentFields={['house', 'postalCode']}
                                            searchErrorText={errorText}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column width={2}>
                                    <Form.Field error={errors[`${name}Address.house`]}>
                                        <label>
                                            Дом, корпус
                                        </label>
                                        <InputDropdown
                                            name={'house'}
                                            nameOption={'fullHouseAddress'}
                                            url={'parcels/addresses/houseAddressSuggestions'}
                                            disabled={!isCreate}
                                            value={(form[`${name}Address`] || {}).fullHouseAddress || ''}
                                            onChange={changeCity}
                                            params={form[`${name}Address`]}
                                            ignoredFields={['city', 'street']}
                                            paramsForSearchFields={[['city', 'cityName'], ['countryCode'], ['street', 'streetName']]}
                                            searchErrorText={errorText}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column width={2}>
                                    <Form.Field error={errors[`${name}Address.office`]}>
                                        <label>
                                            Квартира/офис
                                        </label>
                                        <Input
                                            disabled={!isCreate}
                                            value={(form[`${name}Address`] || {}).office || ''}
                                            onChange={(e, {value}) => changeCity('office', value)}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column width={2}>
                                    <Form.Field error={errors[`${name}Address.postalCode`]}>
                                        <label>
                                            Индекс
                                        </label>
                                        <Input
                                            disabled={!isCreate || postalCodeIdDisabled}
                                            value={(form[`${name}Address`] || {}).postalCode || ''}
                                            onChange={(e, {value}) => changeCity('postalCode', value)}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                            </Fragment>
                        )}
                    </Grid.Row>

                    {requiresPickupPointCode && isCreate && (
                        <Grid.Row columns={16}>
                            <Grid.Column width={16} textAlign='right'>
                                <Button
                                    type='button'
                                    size='small'
                                    disabled={!isCreate}
                                    onClick={() => setIsPvzMapOpen(true)}
                                >
                                    Выбрать ПВЗ на карте
                                </Button>
                            </Grid.Column>
                        </Grid.Row>
                    )}

                    {!requiresPickupPointCode && (
                        <Grid.Row columns={16}>
                            <Grid.Column width={16}>
                                <Form.Field error={errors[`${name}AddressComment`]} className='border-bottom'>
                                    <label>
                                        Комментарий к адресу
                                    </label>
                                    <TextArea
                                        disabled={!isCreate}
                                        placeholder={'Этаж, подъезд, часы работы или другая информация'}
                                        value={form[`${name}AddressComment`] || ''}
                                        maxLength={getAddressCommentMaxLength()}
                                        onChange={(e, {value}) => changeParams({
                                            [`${name}AddressComment`]: value
                                        })}
                                        rows={3}
                                        style={{
                                            minHeight: 'auto',
                                            resize: 'vertical'
                                        }}
                                    />
                                    {isCSECarrier() && (
                                        <div style={{fontSize: '12px', color: '#888', marginTop: '4px'}}>
                                            Максимум 100 символов для КСЭ
                                        </div>
                                    )}
                                </Form.Field>
                            </Grid.Column>
                        </Grid.Row>
                    )}

                    {requiresPickupPointCode && isCreate && (
                        <Grid.Row columns={16}>
                            <Grid.Column width={16}>
                                <div style={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#135ca9',
                                    marginBottom: '8px'
                                }}
                                >
                                    Вы можете указать:
                                </div>
                                <div style={{marginTop: '4px', fontSize: '12px', color: '#666'}}>
                                    {nearestPvzHintText}
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                    )}

                    {requiresPickupPointCode && isCreate && (
                        <Grid.Row columns={16}>
                            <Grid.Column width={16}>
                                <Form.Field error={errors[`${name}PointCode`]}>
                                    <label>
                                        Адрес/код ПВЗ*
                                    </label>
                                    <Dropdown
                                        fluid
                                        search
                                        selection
                                        selectOnBlur={false}
                                        placeholder={nearestPvzLoading ? 'Загрузка списка ПВЗ...' : 'Выберите адрес из списка ПВЗ'}
                                        loading={nearestPvzLoading}
                                        disabled={!isCreate || nearestPvzLoading || nearestPvzOptions.length === 0}
                                        noResultsMessage={nearestPvzLoading ? 'Загрузка...' : 'Нет подходящих ПВЗ для текущего населённого пункта'}
                                        options={nearestPvzOptions}
                                        value={nearestPvzValue}
                                        onChange={(e, data) => {
                                            const selectedCode = (data && data.value) ? String(data.value).toUpperCase() : '';
                                            setNearestPvzValue(selectedCode || null);
                                            if (!selectedCode) {
                                                setLockedNearestPvz(null);
                                                return;
                                            }
                                            const selected = listForNearestDropdown.find(x => String((x.code || x.Code) || '').toUpperCase() === selectedCode);
                                            if (selected) {
                                                applyPvzSelection(selected, { lockEntries: listForNearestDropdown });
                                            }
                                        }}
                                    />
                                </Form.Field>
                            </Grid.Column>
                        </Grid.Row>
                    )}

                    {requiresPickupPointCode && !isCreate && (
                        <Fragment>
                            <Grid.Row columns={16}>
                                <Grid.Column width={16}>
                                    <Form.Field error={errors[`${name}PointCode`]}>
                                        <label>Код ПВЗ</label>
                                        <Input disabled value={String(form[`${name}PointCode`] || '').toUpperCase()} />
                                    </Form.Field>
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={16}>
                                <Grid.Column width={16}>
                                    <Form.Field>
                                        <label>Адрес ПВЗ</label>
                                        <Input
                                            disabled
                                            value={normalizePvzAddressDisplay((((form[`${name}Address`] || {}).fullName) || ''))}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                            </Grid.Row>
                            {renderPvzInfoBlock()}
                        </Fragment>
                    )}

                    {requiresPickupPointCode && isCreate && pvzUiInfo && renderPvzInfoBlock()}

                    {requiresPickupPointCode ? (
                        <Fragment>
                            <Grid.Row columns={16}>
                                <Grid.Column width={16}>
                                    <div style={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: '#135ca9',
                                        marginBottom: '8px',
                                        marginTop: '8px'
                                    }}
                                    >
                                        Контактные данные согласно удостоверяющего документа:
                                    </div>
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={16}>
                                <Grid.Column width={16}>
                                    <Form.Field error={errors[`${name}Company`]}>
                                        <label>Компания</label>
                                        <Input
                                            disabled={!isCreate}
                                            value={form[`${name}Company`] || ''}
                                            maxLength={50}
                                            onChange={(e, {value}) => changeParams({
                                                [`${name}Company`]: value
                                            })}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns={16}>
                                <Grid.Column width={5}>
                                    <Form.Field error={errors[`${name}ContactLastName`]}>
                                        <label>Фамилия*</label>
                                        <Input
                                            disabled={!isCreate}
                                            value={form[`${name}ContactLastName`] || ''}
                                            maxLength={80}
                                            onChange={(e, {value}) => patchContactFio(`${name}ContactLastName`, value)}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column width={5}>
                                    <Form.Field error={errors[`${name}ContactFirstName`]}>
                                        <label>Имя*</label>
                                        <Input
                                            disabled={!isCreate}
                                            value={form[`${name}ContactFirstName`] || ''}
                                            maxLength={80}
                                            onChange={(e, {value}) => patchContactFio(`${name}ContactFirstName`, value)}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                                <Grid.Column width={6}>
                                    <Form.Field error={errors[`${name}ContactPatronymic`]}>
                                        <label>Отчество</label>
                                        <Input
                                            disabled={!isCreate}
                                            value={form[`${name}ContactPatronymic`] || ''}
                                            maxLength={80}
                                            onChange={(e, {value}) => patchContactFio(`${name}ContactPatronymic`, value)}
                                        />
                                    </Form.Field>
                                </Grid.Column>
                            </Grid.Row>
                        </Fragment>
                    ) : (
                        <Grid.Row columns={16}>
                            <Grid.Column width={16}>
                                <Form.Field error={errors[`${name}ContactPersonName`]}>
                                    <label>
                                        Контактное лицо*
                                    </label>
                                    <Input
                                        disabled={!isCreate}
                                        value={form[`${name}ContactPersonName`] || ''}
                                        maxLength={50}
                                        onChange={(e, {value}) => patchContactPersonName(value)}
                                    />
                                </Form.Field>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    {!requiresPickupPointCode && (
                        <Grid.Row columns={16}>
                            <Grid.Column width={16}>
                                <Form.Field error={errors[`${name}Company`]}>
                                    <label>
                                        Компания
                                        {(name !== 'shipment' && form.selectedTariff && form.selectedTariff.isRecipientCompanyRequired) ?
                                            <span>
                                                *
                                                <Info
                                                    className={'m-l-5'}
                                                    name={'Компания'}
                                                    text={'Это поле является обязательным по условиям ТК'}
                                                />
                                            </span>
                                            : ''}
                                    </label>
                                    <Input
                                        disabled={!isCreate}
                                        value={form[`${name}Company`] || ''}
                                        maxLength={50}
                                        onChange={(e, {value}) => changeParams({
                                            [`${name}Company`]: value
                                        })}
                                    />
                                </Form.Field>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    <Grid.Row columns={16} className='phone-ext-row'>
                        <Grid.Column width={4} style={{paddingRight: '0.25em'}}>
                            <Form.Field error={errors[`${name}PhoneNumber`]}>
                                <label>
                                    Телефон*
                                    {!!phoneHintText && <Info
                                        className={'m-l-5'}
                                        name={'Телефон'}
                                        text={phoneHintText}
                                    />}
                                </label>
                                <SemanticCountryPhoneInput
                                    disabled={!isCreate}
                                    value={form[`${name}PhoneNumber`] || ''}
                                    onChange={(e, {value}) => changeParams({
                                        [`${name}PhoneNumber`]: value
                                    })}
                                    defaultCountry="ru"
                                />
                            </Form.Field>
                        </Grid.Column>
                        {showCourierMessage && <Grid.Column width={12} className='courier-message-field-col'>
                            <Form.Field className='courier-message-field courier-message-field_inline' error={errors['senderComment']}>
                                <label>
                                    Короткое сообщение курьеру
                                    <Info
                                        className='carrier-tariff-inline__icon'
                                        name={'Короткое сообщение курьеру'}
                                        text={courierMessageInfoText}
                                        position={'right'}
                                        offset={[8]}
                                    />
                                </label>
                                <div className='courier-message-field__control'>
                                    <Checkbox
                                        checked={senderCommentEnabled}
                                        onChange={(e, data) => {
                                            changeParams({senderCommentEnabled: !!data.checked});
                                            if (!data.checked) {
                                                changeParams({senderComment: ''});
                                            }
                                        }}
                                    />
                                    <Input
                                        disabled={!senderCommentEnabled}
                                        maxLength={senderCommentMaxLength}
                                        value={senderCommentValue}
                                        placeholder='Например: нужна тележка'
                                        onChange={(e, {value}) => changeParams({senderComment: value})}
                                    />
                                </div>
                                {senderCommentEnabled && (
                                    <div className={`courier-message-field__hint ${isSenderCommentMaxReached ? 'courier-message-field__hint_limit' : ''}`}>
                                        {senderCommentValue.length}/{senderCommentMaxLength}
                                    </div>
                                )}
                            </Form.Field>
                        </Grid.Column>}
                        {showAdditionalPhoneFields && <Grid.Column width={4} style={{paddingLeft: '0.25em'}}>
                            <Form.Field error={errors[`${name}Ext1`]}>
                                <label>
                                    Доб. Номер1 (EXT.)
                                </label>
                                <Input
                                    disabled={!isCreate}
                                    value={form[`${name}Ext1`] || ''}
                                    onChange={(e, {value}) => changeParams({
                                        [`${name}Ext1`]: value
                                    })}
                                    style={{maxWidth: '10ch'}}
                                    maxLength={10}
                                />
                            </Form.Field>
                        </Grid.Column>}
                        {showAdditionalPhoneFields && <Grid.Column width={8}/>}
                        {!showAdditionalPhoneFields && !showCourierMessage && <Grid.Column width={12}/>}
                    </Grid.Row>
                    {requiresPickupPointCode && (
                        <Grid.Row columns={16}>
                            <Grid.Column width={8}>
                                {(() => {
                                    const emailFieldKey = `${name}Email`;
                                    const emailValue = form[emailFieldKey] || '';
                                    const emailError = isCreate ? (errors[emailFieldKey] || null) : null;
                                    const emailTooltip = isCreate
                                        ? (emailError || (!String(emailValue).trim() ? EMAIL_FORMAT_HINT : null))
                                        : null;
                                    return (
                                <Form.Field className={emailError ? 'error-style' : ''}>
                                    <label>
                                        {name === 'shipment' ? 'Эл.почта отправителя' : 'Эл.почта получателя'}
                                        <Info
                                            className={'m-l-5'}
                                            name={name === 'shipment' ? 'Эл.почта отправителя' : 'Эл.почта получателя'}
                                            text={name === 'shipment'
                                                ? 'Рекомендуем указать электронный адрес отправителя посылки, если им является лицо отличное от Вас. Т.е. адрес человека, который отнесет посылку в ПВЗ. После оформления посылки на этот адрес будут отправлены подробности по отправлению. Если Вы дополнительно установите чек-бокс «Дублировать Автору» — сообщение будет отослано и Вам. Если Вы не укажите адрес отправителя, это сообщение будет отправлено только Вам.'
                                                : 'Рекомендуем указать электронный адрес получателя для отправки уведомлений о статусе посылки (как правило эти уведомления более своевременны, чем информация поступающая в СМС от самой ТК). Если Вы дополнительно установите чек-бокс «Дублировать Автору» — информация будет отсылаться и Вам. Если Вы не укажите адрес получателя, информация о статусах посылки будет приходить только Вам.'}
                                        />
                                    </label>
                                    <Popup
                                        content={emailTooltip}
                                        position='top left'
                                        on='hover'
                                        mouseEnterDelay={200}
                                        disabled={!emailTooltip}
                                        trigger={(
                                            <div>
                                                <SemanticEmailInput
                                                    value={emailValue}
                                                    disabled={!isCreate}
                                                    onChange={(e, {value}) => {
                                                        const patch = {[emailFieldKey]: value};
                                                        if (!String(value || '').trim()) {
                                                            patch[`${name}EmailDuplicateToAuthor`] = false;
                                                        }
                                                        if (setFieldError) {
                                                            setFieldError(emailFieldKey, null);
                                                        }
                                                        changeParams(patch);
                                                    }}
                                                    onBlur={() => {
                                                        if (!isCreate) return;
                                                        const currentValue = (formRef.current || form)[emailFieldKey];
                                                        const trimmed = currentValue != null ? String(currentValue).trim() : '';
                                                        if (trimmed !== (currentValue || '')) {
                                                            changeParams({[emailFieldKey]: trimmed});
                                                        }
                                                        if (setFieldError) {
                                                            setFieldError(emailFieldKey, getEmailValidationError(trimmed));
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                    />
                                </Form.Field>
                                    );
                                })()}
                            </Grid.Column>
                            <Grid.Column width={8} style={{display: 'flex', alignItems: 'flex-end', paddingBottom: '12px'}}>
                                <Checkbox
                                    label="Дублировать автору"
                                    checked={!!form[`${name}EmailDuplicateToAuthor`]}
                                    disabled={!isCreate || !String(form[`${name}Email`] || '').trim()}
                                    onChange={(e, {checked}) => changeParams({
                                        [`${name}EmailDuplicateToAuthor`]: !!checked
                                    })}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    {showAdditionalPhoneFields && <Grid.Row columns={16} className='phone-ext-row'>
                        <Grid.Column width={4} style={{paddingRight: '0.25em'}}>
                            <Form.Field error={errors[`${name}AdditionalPhone`]}>
                                <label>
                                    Дополнительный телефон
                                </label>
                                <SemanticCountryPhoneInput
                                    disabled={!isCreate}
                                    value={form[`${name}AdditionalPhone`] || ''}
                                    onChange={(e, {value}) => changeParams({
                                        [`${name}AdditionalPhone`]: value
                                    })}
                                    defaultCountry="ru"
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column width={4} style={{paddingLeft: '0.25em'}}>
                            <Form.Field error={errors[`${name}Ext2`]}>
                                <label>
                                    Доб. номер2 (EXT.)
                                </label>
                                <Input
                                    disabled={!isCreate}
                                    value={form[`${name}Ext2`] || ''}
                                    onChange={(e, {value}) => changeParams({
                                        [`${name}Ext2`]: value
                                    })}
                                    style={{maxWidth: '10ch'}}
                                    maxLength={10}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column width={8}/>
                    </Grid.Row>}
                </Grid>
            </Form>

            <ConfirmModal
                label={'Адрес одной строкой'}
                onConfirm={() => stringToAddress(stringAddress, setStringAddress, (data) => changeParams({
                    [`${name}Address`]: data
                }))}
                open={isOpenStringAddressModal}
                setOpen={setIsOpenStringAddressModal}
            >
                <Form>
                    <Grid>
                        <Grid.Row columns={16}>
                            <Grid.Column width={16}>
                                <Form.Field>
                                    <label>
                                        Адрес
                                    </label>
                                    <Input
                                        placeholder={'Введите или скопируйте адрес одной строкой'}
                                        value={stringAddress || ''}
                                        onChange={(e, {value}) => setStringAddress(value)}
                                    />
                                </Form.Field>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Form>
            </ConfirmModal>

            <SelectPointModal
                label={'Мои адреса'}
                open={isOpenAddressBookModal}
                setOpen={setIsOpenAddressBookModal}
                companyId={companyId}
                pvzOnly={requiresPickupPointCode}
                setPoint={(e, {value}) => onChangeAddress(name, value, true)}
            />

            {requiresPickupPointCode && (
                <PvzSelectMapModal
                    open={isPvzMapOpen}
                    onClose={() => setIsPvzMapOpen(false)}
                    postalCode={pvzSearchLocation.postalCode}
                    city={pvzSearchLocation.city}
                    centerLat={pvzSearchLocation.centerLat}
                    centerLon={pvzSearchLocation.centerLon}
                    weight={resolveParcelWeightForPvz(form)}
                    isReception={name === 'shipment'}
                    onSelect={(p, opts) => {
                        applyPvzSelection(p, opts);
                        setIsPvzMapOpen(false);
                    }}
                />
            )}
        </SectionForm>
    );
}

export default PointSection;


// WEBPACK FOOTER //
// ./App/pages/parcels/form_v3/pointSection.jsx